import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { AiListIngredientMenuComponent } from '@app/lists/components/ai-list-ingredient-menu/ai-list-ingredient-menu.component';
import { AIListBuilderViewModel } from '@app/lists/model/ai-list-builder-view.model';
import { AIListProductCardViewModel } from '@app/lists/model/ai-list-product-card.model';
import { AIListBuilderService } from '@app/lists/services/ai-list-builder.service';
import { ListsOverlayService } from '@app/lists/services/lists-overlay.service';
import { MenuController, ViewWillEnter, ViewWillLeave } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { FilterOption } from '@shared/components/filter-modal/filter-modal.component';
import {
  AIListAnalyticsService,
  AIListService,
  IAIListFilterState,
} from '@usf/ngrx-list-ai';
import { first, Observable } from 'rxjs';

@Component({
  selector: 'app-ai-list',
  templateUrl: './ai-list.page.html',
  styleUrls: ['./ai-list.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiListPage implements ViewWillEnter, ViewWillLeave {
  viewModel$: Observable<AIListBuilderViewModel>;
  searchKey = '';
  previousSearchKey = '';
  platformRef = PlatformEnum;
  @ViewChild(AiListIngredientMenuComponent)
  ingredientMenuComponent: AiListIngredientMenuComponent;

  constructor(
    private readonly panAppState: PanAppState,
    private readonly translateService: TranslateService,
    private readonly aiListBuilderService: AIListBuilderService,
    private readonly aiListService: AIListService,
    readonly platformService: PlatformService,
    private readonly menuController: MenuController,
    private readonly listsOverlayService: ListsOverlayService,
    private readonly aiListAnalyticsService: AIListAnalyticsService,
  ) {
    this.viewModel$ = this.aiListBuilderService.aiListBuilderViewModel$;
  }

  ionViewWillEnter(): void {
    this.panAppState.setPageTitle(
      this.translateService.instant('i18n.lists.aiListBuilder'),
    );
    this.viewModel$
      .pipe(
        first(
          viewModel =>
            viewModel?.aiListId &&
            !viewModel?.fileParse?.isParsingFiles &&
            !viewModel?.productMatch?.isMatchingProducts,
        ),
      )
      .subscribe(viewModel => {
        this.aiListAnalyticsService.trackAIListPageLoad(viewModel.aiListId);
      });
  }

  ionViewWillLeave(): void {
    this.clearFilters();
  }

  trackItem(index: number, item: AIListProductCardViewModel) {
    return item ? item.aiListItem?.pimInfo + index : index;
  }

  async openIngredientMenu(
    item: AIListProductCardViewModel,
    viewModel: AIListBuilderViewModel,
  ) {
    this.aiListService.searchIngredients(
      {
        ingredientName: item.ingredientDisplayName,
        pimInfo: item.aiListItem?.pimInfo,
      },
      item.aiListItem,
    );
    await this.menuController.open('ingredient-menu-' + viewModel?.aiListId);
  }

  /**
   * Handles searching in the list
   * @param event - The search event
   */
  onSearch(event: any) {
    if (this.searchKey !== this.previousSearchKey) {
      this.aiListBuilderService.searchInList(this.searchKey);
      this.previousSearchKey = this.searchKey;
    }
  }

  resetSearch() {
    this.searchKey = '';
    this.aiListBuilderService.resetSearch();
  }

  /**
   * Opens the filter modal with the provided options
   * @param filterOptions - The filter options to display in the modal
   */
  async openFilterModal(filterOptions: FilterOption[]) {
    const modal =
      await this.listsOverlayService.openAiFilterModal(filterOptions);

    const { data } = await modal.onDidDismiss();
    if (data && data.action !== 'close') {
      const filterState: IAIListFilterState = {
        direct: false,
        earlyCutoff: false,
        discontinuedWhenOut: false,
        exclusiveBrands: false,
      };

      for (const option of data.options) {
        if (option.selected) {
          filterState[option.id as keyof IAIListFilterState] = true;
        }
      }
      this.aiListBuilderService.updateFilterOptions(filterState);
    }
  }

  clearFilters() {
    this.aiListBuilderService.updateFilterOptions({
      direct: false,
      earlyCutoff: false,
      discontinuedWhenOut: false,
      exclusiveBrands: false,
    } as IAIListFilterState);
  }

  onMenuDidClose(): void {
    if (this.ingredientMenuComponent) {
      this.ingredientMenuComponent.resetMenuState();
    }
  }

  async openCompleteListModal(viewModel: AIListBuilderViewModel) {
    const confirmed = await this.listsOverlayService.openCompleteListModal();
    if (confirmed) {
      this.completeList(viewModel);
    }
  }

  private completeList(viewModel: AIListBuilderViewModel) {
    if (viewModel?.aiListId) {
      this.aiListBuilderService.completeAiList(viewModel.aiListId);
    }
  }

  /**
   * Handles deleting an ingredient from the AI list
   */
  async onDeleteIngredient(
    item: AIListProductCardViewModel,
    viewModel: AIListBuilderViewModel,
  ) {
    await this.listsOverlayService.openDeleteAIListIngredientModal(
      item,
      viewModel,
      this.aiListService,
    );
  }
}
